import React, { useState, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { ReactComponent as PairCareIcon } from '../assets/svgs/PairSelfCareIcon.svg';
import { CARD_THEMES, buildCardModel } from '../modules/printDocuments';
import { saveCardPDF } from '../modules/pdfExport';
import PropTypes from 'prop-types';

/**
 * Check if a canvas is blank (all white or transparent pixels).
 * Compares the canvas to a blank reference to detect if drawing was incomplete.
 * @param {HTMLCanvasElement} canvas - Canvas to check
 * @returns {boolean} True if canvas appears blank
 */
function isCanvasBlank(canvas) {
  if (!canvas) return true;

  try {
    // Get image data from canvas
    const ctx = canvas.getContext('2d');
    if (!ctx) return true;

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    // Check if all pixels are white or transparent
    // A non-blank canvas should have at least some dark pixels
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];

      // If we find a non-white pixel with significant alpha, canvas is not blank
      if (a > 200 && (r < 250 || g < 250 || b < 250)) {
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Error checking canvas blank state:', error);
    return true; // Assume blank on error
  }
}

/**
 * ProfileBusinessCard component: Displays a business card with theme selector.
 * Card size: 3.5 x 2 inches (standard US business card)
 *
 * Props:
 * - provider: The provider object
 * - publicProfileUrl: The full public profile URL
 * - selectedTheme: Index into CARD_THEMES (default: 0)
 * - onThemeChange: Callback when theme changes (optional)
 * - onError: Optional callback when PDF save fails, receives error message
 *
 * Exposed methods (via ref):
 * - print(): Trigger print dialog
 * - saveAsPDF(): Trigger PDF export
 */
const ProfileBusinessCard = forwardRef(function ProfileBusinessCard({
  provider,
  publicProfileUrl,
  selectedTheme = 0,
  onThemeChange,
  onError
}, ref) {
  const [themeIndex, setThemeIndex] = useState(selectedTheme);
  const qrCodeRef = useRef();

  const cardModel = provider ? buildCardModel(provider, publicProfileUrl, themeIndex) : null;
  const theme = cardModel?.theme;

  const handleThemeChange = (event) => {
    const newIndex = event.target.value;
    setThemeIndex(newIndex);
    if (onThemeChange) {
      onThemeChange(newIndex);
    }
  };

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const handleSaveAsPDF = useCallback(async () => {
    try {
      // Capture QR code at click time (not on mount) to avoid race condition
      if (!qrCodeRef.current) {
        throw new Error('QR code reference not found.');
      }

      const canvas = qrCodeRef.current.querySelector('canvas');
      if (!canvas) {
        throw new Error('QR code canvas element not found. Unable to generate PDF with QR code.');
      }

      // Give the QR code canvas time to render and composite the centre icon
      // The QRCodeCanvas with imageSettings loads the icon asynchronously
      await new Promise(resolve => setTimeout(resolve, 200));

      // Capture the canvas data
      const capturedDataUrl = canvas.toDataURL('image/png');
      if (!capturedDataUrl) {
        throw new Error('Failed to capture QR code image data. Unable to generate PDF with QR code.');
      }

      // Verify the canvas is not blank (icon was rendered)
      if (isCanvasBlank(canvas)) {
        throw new Error('QR code canvas appears blank. The centre icon may not have loaded. Please try again.');
      }

      // Save PDF with the captured QR code
      await saveCardPDF(provider, publicProfileUrl, themeIndex, CARD_THEMES, capturedDataUrl);
    } catch (error) {
      console.error('Failed to save PDF:', error);
      const errorMessage = error.message || 'Failed to generate PDF. Please try again.';
      if (onError) {
        onError(errorMessage);
      }
    }
  }, [provider, publicProfileUrl, themeIndex, onError]);

  // Expose print and saveAsPDF methods via ref
  useImperativeHandle(ref, () => ({
    print: handlePrint,
    saveAsPDF: handleSaveAsPDF
  }), [handlePrint, handleSaveAsPDF]);

  if (!provider || !cardModel) {
    return <div>No provider data available</div>;
  }

  // Parse theme colors
  const bgColor = theme.background.replace('#', '').match(/.{1,2}/g);
  const backgroundColor = `rgb(${parseInt(bgColor[0], 16)}, ${parseInt(bgColor[1], 16)}, ${parseInt(bgColor[2], 16)})`;

  const textColor = theme.text.replace('#', '').match(/.{1,2}/g);
  const textColorRGB = `rgb(${parseInt(textColor[0], 16)}, ${parseInt(textColor[1], 16)}, ${parseInt(textColor[2], 16)})`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Theme Selector */}
      <FormControl sx={{ minWidth: 200 }}>
        <InputLabel>Card Theme</InputLabel>
        <Select
          value={themeIndex}
          onChange={handleThemeChange}
          label="Card Theme"
        >
          {CARD_THEMES.map((t, index) => (
            <MenuItem key={index} value={index}>
              {t.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Hidden QR code for PDF export (high-resolution canvas) */}
      <div
        ref={qrCodeRef}
        style={{
          display: 'none'
        }}
      >
        {publicProfileUrl && (
          <QRCodeCanvas
            value={publicProfileUrl}
            level="H"
            size={1000}
            marginSize={4}
            imageSettings={{
              src: require('../assets/svgs/PairSelfCareIcon.png'),
              height: 200,
              width: 200,
              excavate: true
            }}
          />
        )}
      </div>

      {/* Business Card Preview */}
      <Box
        sx={{
          width: '3.5in',
          height: '2in',
          backgroundColor,
          color: textColorRGB,
          padding: '0.15in',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          fontFamily: 'Arial, sans-serif',
          fontSize: '11px',
          '@media print': {
            boxShadow: 'none',
            borderRadius: 0,
            margin: '0.5in auto',
            pageBreakAfter: 'always'
          }
        }}
      >
        {/* Left side: Text content */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          {/* Business Name */}
          <Box sx={{ fontSize: '16px', fontWeight: 'bold', lineHeight: '1.2' }}>
            {cardModel.name || 'Business Name'}
          </Box>

          {/* Short Description - below name, max 2 lines with ellipsis */}
          {cardModel.shortDescription && (
            <Box
              sx={{
                fontSize: '8px',
                lineHeight: '1.3',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                textOverflow: 'ellipsis'
              }}
            >
              {cardModel.shortDescription}
            </Box>
          )}

          {/* Contact Information */}
          <Box sx={{ fontSize: '9px', lineHeight: '1.4' }}>
            {cardModel.phone && (
              <div>
                Phone: {cardModel.phone}
              </div>
            )}
            {cardModel.email && (
              <div>
                Email: {cardModel.email}
              </div>
            )}
            {cardModel.address && (
              <div style={{ marginTop: '4px', fontSize: '8px' }}>
                {cardModel.address}
              </div>
            )}
          </Box>

          {/* Website URL with icon */}
          {cardModel.url && (
            <Box
              sx={{
                fontSize: '7px',
                fontStyle: 'italic',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                borderTop: `1px solid ${theme.accent}`,
                paddingTop: '4px',
                marginTop: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '2px'
              }}
            >
              <PairCareIcon style={{ width: '7px', height: '7px', flexShrink: 0 }} />
              {cardModel.url}
            </Box>
          )}
        </Box>

        {/* Right side: QR Code */}
        {publicProfileUrl && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: '0.1in',
              padding: '4px',
              backgroundColor: '#ffffff',
              borderRadius: '2px',
              width: '88px',
              height: '88px',
              alignSelf: 'center',
              flex: '0 0 auto'
            }}
          >
            <QRCodeSVG
              value={publicProfileUrl}
              level="H"
              size={80}
              marginSize={4}
              imageSettings={{
                src: require('../assets/svgs/PairSelfCareIcon.png'),
                height: 16,
                width: 16,
                excavate: true
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
});

ProfileBusinessCard.propTypes = {
  provider: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    friendlyUrl: PropTypes.string,
    phoneNumber: PropTypes.string,
    email: PropTypes.string,
    shortDescription: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postalCode: PropTypes.string
  }),
  publicProfileUrl: PropTypes.string.isRequired,
  selectedTheme: PropTypes.number,
  onThemeChange: PropTypes.func,
  onError: PropTypes.func
};

export default ProfileBusinessCard;
